import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { useThree, useLoader, useFrame } from '@react-three/fiber';
import { getProjectByEkranId } from './data/projects';

// ============================================================================
// LAMPA MATERIAL - module scope, bir marta yaratiladi
// ============================================================================
let cachedLampMaterial = null;
const getLampMaterial = () => {
  if (cachedLampMaterial) return cachedLampMaterial;

  cachedLampMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color("white") },
      uIntensity: { value: 30.0 }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uIntensity;
      void main() {
        gl_FragColor = vec4(uColor * uIntensity, 1.0);
      }
    `,
    transparent: false,
    toneMapped: false
  });

  return cachedLampMaterial;
};

// ============================================================================
// SCREEN KOMPONENTI - Alohida, o'z state bilan (memoized)
// ============================================================================
const Screen = memo(function Screen({
  ekranId,
  geometry,
  position,
  rotation,
  scale,
  texture,
  onProjectClick,
  interactionsDisabled
}) {
  const [hovered, setHovered] = useState(false);

  const handlePointerOver = useCallback((e) => {
    if (interactionsDisabled) return;
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = "pointer";
  }, [interactionsDisabled]);

  const handlePointerOut = useCallback((e) => {
    if (interactionsDisabled) return;
    e.stopPropagation();
    setHovered(false);
    document.body.style.cursor = "default";
  }, [interactionsDisabled]);

  const handleClick = useCallback(() => {
    if (interactionsDisabled) return;
    const project = getProjectByEkranId(ekranId);
    if (project && onProjectClick) {
      onProjectClick(project.id);
    }
  }, [ekranId, onProjectClick, interactionsDisabled]);

  // interactionsDisabled bo'lganda hover'ni reset qilish
  useEffect(() => {
    if (interactionsDisabled && hovered) {
      setHovered(false);
      document.body.style.cursor = "default";
    }
  }, [interactionsDisabled, hovered]);

  const material = useMemo(() => (
    <meshBasicMaterial
      map={texture}
      toneMapped={false}
      color={hovered ? "gray" : "white"}
    />
  ), [texture, hovered]);

  return (
    <mesh
      name={ekranId}
      geometry={geometry}
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onClick={handleClick}
    >
      {material}
    </mesh>
  );
});

// ============================================================================
// ASOSIY MODEL3 KOMPONENTI
// ============================================================================
export default function Model3({ onProjectClick, ...props }) {
  const { nodes, materials } = useGLTF('/model3.glb')

  // ============================================================================
  // SECTION-6 UCHUN INTERAKSIYALARNI O'CHIRISH
  // ============================================================================
  const [interactionsDisabled, setInteractionsDisabled] = useState(false);

  useEffect(() => {
    const checkSection6 = () => {
      const section6 = document.querySelector('.section-6');
      const section7 = document.querySelector('.section-7');
      
      if (!section6 || !section7) return;
      
      const section6Rect = section6.getBoundingClientRect();
      const section7Rect = section7.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Section-6 ko'rinib turganda va Section-7 hali ko'rinmagan bo'lsa
      // interaksiyalarni o'chirish
      const inSection6 = section6Rect.top < viewportHeight && section6Rect.bottom > 0;
      const section7Visible = section7Rect.top < viewportHeight * 0.5;
      
      setInteractionsDisabled(inSection6 && !section7Visible);
    };

    // Dastlabki tekshirish
    checkSection6();

    // Scroll listener
    window.addEventListener('scroll', checkSection6, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', checkSection6);
    };
  }, []);

  // ============================================================================
  // SHIFT MATERIAL SOZLAMALARI (bir marta)
  // ============================================================================
  useMemo(() => {
    materials['Material.001'].metalness = 0.8;
    materials['Material.001'].roughness = 1;
    materials['Material.001'].color.set("#222222");
    materials['Material.001'].needsUpdate = true;
  }, [materials]);

  // Cached lamp material
  const lampMaterial = useMemo(() => getLampMaterial(), []);

  // ============================================================================
  // VIDEO TEXTURE - lazy loaded
  // ============================================================================
  const [video1Texture, setVideo1Texture] = useState(null);
  const videoLoadedRef = useRef(false);
  const videoElementRef = useRef(null);

  useEffect(() => {
    const loadVideo = () => {
      if (videoLoadedRef.current) return;
      videoLoadedRef.current = true;

      const video = document.createElement("video");
      video.src = process.env.PUBLIC_URL + "/img/endd.mp4";
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      // video.playbackRate = 1;
      video.preload = "metadata";
      video.rotation = Math.PI / -2;
      videoElementRef.current = video;

      video.oncanplaythrough = () => {
        video.play();
        const texture = new THREE.VideoTexture(video);
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(1, -1);
        texture.rotation = 0;
        setVideo1Texture(texture);
      };

      video.load();
    };

    const timeout = setTimeout(loadVideo, 3000);

    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 2) {
        loadVideo();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      // Video va texture ni tozalash
      if (videoElementRef.current) {
        videoElementRef.current.pause();
        videoElementRef.current.removeAttribute('src');
        videoElementRef.current.load();
        videoElementRef.current = null;
      }
    };
  }, []);

  // ============================================================================
  // DEVOR TEXTURE
  // ============================================================================
  const texture2Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Wall.jpg"
  );

  useMemo(() => {
    texture2Img.wrapS = THREE.RepeatWrapping;
    texture2Img.wrapT = THREE.RepeatWrapping;
    texture2Img.rotation = Math.PI / -2;
    texture2Img.repeat.set(2.4, 1);
  }, [texture2Img]);

  // ============================================================================
  // EKRAN RASMLARI (18 ta) - LAZY LOADING
  // ============================================================================
  const renderer = useThree((state) => state.gl);
  const [loadedTextures, setLoadedTextures] = useState({});
  const textureLoadingRef = useRef({});
  const textureLoader = useMemo(() => new THREE.TextureLoader(), []);

  // Placeholder texture (kulrang rang)
  const placeholderTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, 64, 64);
    // Loading indicator
    ctx.fillStyle = '#333333';
    ctx.fillRect(20, 28, 24, 8);
    const tex = new THREE.CanvasTexture(canvas);
    tex.flipY = false;
    tex.repeat.set(-1, -1);
    return tex;
  }, []);

  // Texture yuklash funksiyasi - loadedTextures ni dependency dan olib tashladik
  // textureLoadingRef orqali allaqachon yuklanganini tekshiramiz
  const loadTexture = useCallback((index) => {
    if (textureLoadingRef.current[index]) return;

    textureLoadingRef.current[index] = true;
    const url = process.env.PUBLIC_URL + `/img/img/a${index + 1}.jpg`;

    textureLoader.load(
      url,
      (tex) => {
        const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
        tex.flipY = false;
        tex.rotation = 0;
        tex.center.set(0.5, 0.5);
        tex.generateMipmaps = true;
        tex.minFilter = THREE.LinearMipMapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.anisotropy = maxAnisotropy;
        tex.repeat.set(-1, -1);

        setLoadedTextures(prev => ({ ...prev, [index]: tex }));
      },
      undefined,
      (error) => {
        console.warn(`Texture ${index + 1} yuklanmadi:`, error);
        textureLoadingRef.current[index] = false;
      }
    );
  }, [renderer, textureLoader]);

  // Texture olish funksiyasi (yuklangan yoki placeholder)
  const getTexture = useCallback((index) => {
    return loadedTextures[index] || placeholderTexture;
  }, [loadedTextures, placeholderTexture]);

  // Scroll-ga asoslangan progressiv lazy loading
  useEffect(() => {
    const totalTextures = 18;
    let lastScrollY = 0;
    let scrollVelocity = 0;
    let rafId = null;

    // Section chegaralari (scroll progress 0-1)
    // Section 2 da birinchi 4 ta, section 7 gacha hammasi yuklangan bo'lishi kerak
    const getTextureCountByProgress = (progress) => {
      if (progress < 0.15) return 0;        // Section 1 - hech narsa
      if (progress < 0.25) return 4;        // Section 2 - birinchi 4 ta
      if (progress < 0.35) return 8;        // Section 3-4 - 8 ta
      if (progress < 0.45) return 12;       // Section 5 - 12 ta
      if (progress < 0.55) return 16;       // Section 6 - 16 ta
      return totalTextures;                  // Section 7+ - hammasi
    };

    // Bir nechta texture-ni parallel yuklash
    const loadTexturesUpTo = (count) => {
      for (let i = 0; i < count; i++) {
        loadTexture(i);
      }
    };

    // Scroll progress hisoblash
    const getScrollProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      return scrollHeight > 0 ? window.scrollY / scrollHeight : 0;
    };

    // Scroll tezligiga qarab qo'shimcha texture-lar yuklash
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollVelocity = Math.abs(currentScrollY - lastScrollY);
      lastScrollY = currentScrollY;

      const progress = getScrollProgress();
      let targetCount = getTextureCountByProgress(progress);

      // Agar tez scroll qilayotgan bo'lsa, oldinda turgan texture-larni ham yuklash
      if (scrollVelocity > 50) {
        // Tez scroll = qo'shimcha 4 ta texture oldindan yuklash
        targetCount = Math.min(targetCount + 4, totalTextures);
      }
      if (scrollVelocity > 100) {
        // Juda tez scroll = qo'shimcha 8 ta
        targetCount = Math.min(targetCount + 8, totalTextures);
      }

      loadTexturesUpTo(targetCount);
    };

    // RAF bilan optimallashtirilgan scroll handler
    const throttledScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        handleScroll();
        rafId = null;
      });
    };

    // Dastlabki holatni tekshirish (agar sahifa o'rtasida refresh bo'lsa)
    const initialProgress = getScrollProgress();
    if (initialProgress > 0.1) {
      loadTexturesUpTo(getTextureCountByProgress(initialProgress));
    }

    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [loadTexture]);

  // ============================================================================
  // DEVOR OXIRI TEXTURE
  // ============================================================================
  const texture5Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Wall-end.jpg"
  );

  useMemo(() => {
    texture5Img.wrapS = THREE.RepeatWrapping;
    texture5Img.wrapT = THREE.RepeatWrapping;
    texture5Img.rotation = Math.PI / -2;
    texture5Img.repeat.set(2, 1);
  }, [texture5Img]);

  // ============================================================================
  // SHIFT (ROOF) TEXTURE
  // ============================================================================
  const texture6Img = useLoader(
    THREE.TextureLoader,
    process.env.PUBLIC_URL + "/img/Roof.jpg"
  );

  useMemo(() => {
    texture6Img.wrapS = THREE.RepeatWrapping;
    texture6Img.wrapT = THREE.RepeatWrapping;
    texture6Img.rotation = Math.PI / -1;
    texture6Img.repeat.set(1, 1);
  }, [texture6Img]);

  // ============================================================================
  // MEMOIZED MATERIALS
  // ============================================================================
  const floorWhiteMaterial = useMemo(() => <meshBasicMaterial color={'white'} />, []);
  const floorGrayMaterial = useMemo(() => <meshBasicMaterial color={'#888888'} />, []);
  const floorDarkMaterial = useMemo(() => <meshBasicMaterial color={'#222222'} />, []);
  const urtaRekaMaterial = useMemo(() => <meshBasicMaterial color="#444444" />, []);
  const wallMaterial = useMemo(() => <meshBasicMaterial map={texture2Img} />, [texture2Img]);
  const roofMaterial = useMemo(() => <meshBasicMaterial map={texture6Img} />, [texture6Img]);
  const endWallMaterial = useMemo(() => <meshBasicMaterial map={texture5Img} side={THREE.DoubleSide} />, [texture5Img]);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <group {...props} dispose={null}>
      <group name="Scene">

        {/* PILLAR - CHAP (Boshlanish) */}
        <group name="pillar_left" position={[-8.054, 0, 2.021]} rotation={[0, 1.571, 0]} />

        {/* SCREEN 1 */}
        <mesh
          name="screen1"
          geometry={nodes.screen1.geometry}
          material={materials['screen1.002']}
          position={[0, 0, -4.945]}
        >
          <Screen
            ekranId="ekran1"
            geometry={nodes.ekran1.geometry}
            position={[-2.994, 0.15, 5.01]}
            rotation={[Math.PI, -Math.PI / 6, Math.PI]}
            scale={[1.26, 1.2, 0.6]}
            texture={getTexture(1)}
            onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
          />
          <mesh
            name="screenLight1"
            geometry={nodes.screenLight1.geometry}
            material={materials['screen-light']}
            position={[-3.006, 0.072, 4.99]}
            rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
            scale={[0.048, 0.009, 1.38]}
          />
        </mesh>

        {/* SCREEN 2 */}
        <mesh
          name="screen2"
          geometry={nodes.screen2.geometry}
          material={materials['screen2.002']}
        >
          <Screen
            ekranId="ekran2"
            geometry={nodes.ekran2.geometry}
            position={[2.994, 0.15, -4.99]}
            rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
            scale={[1.26, 1.2, 0.6]}
            texture={getTexture(2)}
            onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
          />
          <mesh
            name="screenLight2"
            geometry={nodes.screenLight2.geometry}
            material={materials['screen-light.002']}
            position={[3.009, 0.072, -5.016]}
            rotation={[0, Math.PI / 3, 0]}
            scale={[0.048, 0.009, 1.38]}
          />
        </mesh>

        {/* PILLAR - CHAP (Davomi) */}
        <group name="pillar_left001" position={[-8.054, 0, -17.92]} rotation={[0, 1.571, 0]} />
        <group name="new-arch003" position={[0, 0, -19.941]} />

        {/* POL VA SHIFT STRUKTURASI */}
        <group name="new-arch005" position={[0, 0, -19.941]}>
          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0.01, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorWhiteMaterial}
          </mesh>

          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[-12.3, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[-12.43, 16.1, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0, -0.02, -172.67]}
            rotation={[0, 1.57, -Math.PI]}
            scale={[-1, -3, -2]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-chap"
            geometry={nodes['floor-reka-chap'].geometry}
            position={[0, 16.12, -172.67]}
            rotation={[0, 1.57, -Math.PI]}
            scale={[-1, -1, -2]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[0, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorWhiteMaterial}
          </mesh>

          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 0, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 0, -171.46]}
            rotation={[1.57, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[-14.65, 0, -171.48]}
            rotation={[1.57, 0.01, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floor-reka-ung"
            geometry={nodes['floor-reka-ung'].geometry}
            position={[12.4, 16.1, -62.38]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorGrayMaterial}
          </mesh>

          <mesh
            name="floorr"
            geometry={nodes.floorr.geometry}
            position={[0, 0, -62.957]}
            rotation={[0, 0, -Math.PI]}
            scale={[-1, -1, -11.048]}
          >
            {floorDarkMaterial}
          </mesh>

          <mesh
            name="shifr"
            geometry={nodes.shifr.geometry}
            position={[0, 32.575, -57.139]}
            scale={[-1.466, -1, -11.661]}
            doubleSide={true}
          >
            {endWallMaterial}
          </mesh>
        </group>

        {/* SCREEN 3-4 GURUHI */}
        <group name="Empty003" position={[0, 0, -19.941]}>
          <mesh
            name="screen1001"
            geometry={nodes.screen1001.geometry}
            material={materials['screen1.003']}
          >
            <Screen
              ekranId="ekran3"
              geometry={nodes.ekran3.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(3)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1001"
              geometry={nodes.screenLight1001.geometry}
              material={materials['screen-light.002']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2001"
            geometry={nodes.screen2001.geometry}
            material={materials['screen2.003']}
          >
            <Screen
              ekranId="ekran4"
              geometry={nodes.ekran4.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(4)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2001"
              geometry={nodes.screenLight2001.geometry}
              material={materials['screen-light.002']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* O'RTA REKA */}
        <mesh
          name="urta-reka"
          geometry={nodes['urta-reka'].geometry}
          position={[0, 0.017, -82.92]}
          rotation={[0, 0, -Math.PI]}
          scale={[-0.082, -1, -11.048]}
        >
          {urtaRekaMaterial}
        </mesh>

        {/* PILLARLAR */}
        <group name="pillar_left004" position={[-8.054, 0, -57.823]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left005" position={[-8.054, 0, -37.882]} rotation={[0, 1.571, 0]} />
        <group name="new-arch008" position={[0, 0, -59.844]} />

        {/* SCREEN 7-8 GURUHI */}
        <group name="Empty006" position={[0, 0, -59.844]}>
          <mesh
            name="screen1002"
            geometry={nodes.screen1002.geometry}
            material={materials['screen1.004']}
          >
            <Screen
              ekranId="ekran7"
              geometry={nodes.ekran7.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(5)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1002"
              geometry={nodes.screenLight1002.geometry}
              material={materials['screen-light.003']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2002"
            geometry={nodes.screen2002.geometry}
            material={materials['screen2.004']}
          >
            <Screen
              ekranId="ekran8"
              geometry={nodes.ekran8.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(6)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2002"
              geometry={nodes.screenLight2002.geometry}
              material={materials['screen-light.003']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* SCREEN 5-6 GURUHI */}
        <group name="Empty007" position={[0, 0, -39.903]}>
          <mesh
            name="screen1003"
            geometry={nodes.screen1003.geometry}
            material={materials['screen1.005']}
          >
            <Screen
              ekranId="ekran5"
              geometry={nodes.ekran5.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(7)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1003"
              geometry={nodes.screenLight1003.geometry}
              material={materials['screen-light.004']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2003"
            geometry={nodes.screen2003.geometry}
            material={materials['screen2.005']}
          >
            <Screen
              ekranId="ekran6"
              geometry={nodes.ekran6.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(8)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2003"
              geometry={nodes.screenLight2003.geometry}
              material={materials['screen-light.004']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* PILLARLAR - ORQA QISM */}
        <group name="pillar_left010" position={[-8.054, 0, -117.673]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left011" position={[-8.054, 0, -137.614]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left014" position={[-8.054, 0, -97.711]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left015" position={[-8.054, 0, -77.77]} rotation={[0, 1.571, 0]} />
        <group name="new-arch020" position={[0, 0, -99.731]} />

        {/* SCREEN 13-14 GURUHI */}
        <group name="Empty010" position={[0, 0, -119.693]}>
          <mesh
            name="screen1004"
            geometry={nodes.screen1004.geometry}
            material={materials['screen1.006']}
          >
            <Screen
              ekranId="ekran13"
              geometry={nodes.ekran13.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(9)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1004"
              geometry={nodes.screenLight1004.geometry}
              material={materials['screen-light.005']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2004"
            geometry={nodes.screen2004.geometry}
            material={materials['screen2.006']}
          >
            <Screen
              ekranId="ekran14"
              geometry={nodes.ekran14.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(10)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2004"
              geometry={nodes.screenLight2004.geometry}
              material={materials['screen-light.005']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* SCREEN 15-16 GURUHI */}
        <group name="Empty011" position={[0, 0, -139.634]}>
          <mesh
            name="screen1005"
            geometry={nodes.screen1005.geometry}
            material={materials['screen1.007']}
          >
            <Screen
              ekranId="ekran15"
              geometry={nodes.ekran15.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(11)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1005"
              geometry={nodes.screenLight1005.geometry}
              material={materials['screen-light.006']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2005"
            geometry={nodes.screen2005.geometry}
            material={materials['screen2.007']}
          >
            <Screen
              ekranId="ekran16"
              geometry={nodes.ekran16.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(12)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2005"
              geometry={nodes.screenLight2005.geometry}
              material={materials['screen-light.006']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* SCREEN 11-12 GURUHI */}
        <group name="Empty014" position={[0, 0, -99.731]}>
          <mesh
            name="screen1006"
            geometry={nodes.screen1006.geometry}
            material={materials['screen1.008']}
          >
            <Screen
              ekranId="ekran11"
              geometry={nodes.ekran11.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(13)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1006"
              geometry={nodes.screenLight1006.geometry}
              material={materials['screen-light.007']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2006"
            geometry={nodes.screen2006.geometry}
            material={materials['screen2.008']}
          >
            <Screen
              ekranId="ekran12"
              geometry={nodes.ekran12.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(14)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2006"
              geometry={nodes.screenLight2006.geometry}
              material={materials['screen-light.007']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* SCREEN 9-10 GURUHI */}
        <group name="Empty015" position={[0, 0, -79.79]}>
          <mesh
            name="screen1007"
            geometry={nodes.screen1007.geometry}
            material={materials['screen1.009']}
          >
            <Screen
              ekranId="ekran9"
              geometry={nodes.ekran9.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(15)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1007"
              geometry={nodes.screenLight1007.geometry}
              material={materials['screen-light.008']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2007"
            geometry={nodes.screen2007.geometry}
            material={materials['screen2.009']}
          >
            <Screen
              ekranId="ekran10"
              geometry={nodes.ekran10.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(16)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2007"
              geometry={nodes.screenLight2007.geometry}
              material={materials['screen-light.008']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* PILLARLAR - O'NG TOMON */}
        <group name="pillar_left017" position={[5.533, 0, 2.021]} rotation={[0, -0.017, 0]} />
        <group name="pillar_left018" position={[5.533, 0, -18.12]} rotation={[0, -1.567, 0]} />
        <group name="pillar_left019" position={[5.533, 0, -33.728]} rotation={[0, 0.021, 0]} />
        <group name="pillar_left020" position={[5.533, 0, -57.573]} rotation={[0, 1.538, 0]} />
        <group name="pillar_left021" position={[5.533, 0, -77.048]} rotation={[0, 1.538, 0]} />
        <group name="pillar_left022" position={[5.533, 0, -97.236]} rotation={[0, -Math.PI / 2, 0]} />
        <group name="pillar_left023" position={[5.533, 0, -117.643]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left024" position={[5.533, 0, -133.652]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left002" position={[5.533, 0, -173.143]} rotation={[-Math.PI, 1.566, -Math.PI]} />
        <group name="pillar_left003" position={[5.533, 0, -157.134]} rotation={[-Math.PI, 1.566, -Math.PI]} />

        {/* PILLARLAR - CHAP TOMON (OXIRGI) */}
        <group name="pillar_left006" position={[-8.054, 0, -177.105]} rotation={[0, 1.571, 0]} />
        <group name="pillar_left007" position={[-8.054, 0, -157.164]} rotation={[0, 1.571, 0]} />

        {/* SCREEN 17-18 GURUHI (OXIRGI) */}
        <group name="Empty002" position={[0, 0, -159.184]}>
          <mesh
            name="screen1009"
            geometry={nodes.screen1009.geometry}
            material={materials['screen1.010']}
          >
            <Screen
              ekranId="ekran17"
              geometry={nodes.ekran17.geometry}
              position={[-2.994, 0.15, 5.01]}
              rotation={[Math.PI, -Math.PI / 6, Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(17)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight1009"
              geometry={nodes.screenLight1009.geometry}
              material={materials['screen-light.009']}
              position={[-3.006, 0.072, 4.99]}
              rotation={[-Math.PI, Math.PI / 3, -Math.PI]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>

          <mesh
            name="screen2009"
            geometry={nodes.screen2009.geometry}
            material={materials['screen2.010']}
          >
            <Screen
              ekranId="ekran18"
              geometry={nodes.ekran18.geometry}
              position={[2.994, 0.15, -4.99]}
              rotation={[-Math.PI, Math.PI / 6, -Math.PI]}
              scale={[1.26, 1.2, 0.6]}
              texture={getTexture(0)}
              onProjectClick={onProjectClick}
            interactionsDisabled={interactionsDisabled}
            />
            <mesh
              name="screenLight2009"
              geometry={nodes.screenLight2009.geometry}
              material={materials['screen-light.009']}
              position={[3.009, 0.072, -5.016]}
              rotation={[0, Math.PI / 3, 0]}
              scale={[0.048, 0.009, 1.38]}
            />
          </mesh>
        </group>

        {/* DEVORLAR */}
        <mesh
          name="wall-right"
          geometry={nodes['wall-right'].geometry}
          position={[-13.6, 8.092, -84.928]}
          rotation={[0, 0, -1.561]}
          scale={[8.268, 1, 106.705]}
        >
          {wallMaterial}
        </mesh>

        <mesh
          name="devor-oxiri"
          geometry={nodes['devor-oxiri'].geometry}
          position={[-0.043, 8.158, -191.448]}
          rotation={[-Math.PI / 2, 0, 0]}
          scale={[-13.916, -1, -8.294]}
        >
          {roofMaterial}
        </mesh>

        <mesh
          name="wall-left"
          geometry={nodes['wall-left'].geometry}
          position={[13.6, 8.092, -84.928]}
          rotation={[0, 0, Math.PI / 2]}
          scale={[8.268, 1, 106.705]}
        >
          {wallMaterial}
        </mesh>

        {/* KATTA VIDEO EKRAN */}
        <mesh
          name="Screen"
          geometry={nodes.Screen.geometry}
          position={[-0.009, 3.982, -179.999]}
          scale={2.571}
        >
          {video1Texture && <meshBasicMaterial map={video1Texture} />}
        </mesh>

        {/* LAMPALAR - 1-GURUH */}
        <group name="Empty001" position={[0, 0, -40]}>
          <mesh name="light-1-cover002" geometry={nodes['light-1-cover002'].geometry} material={materials['light2-cover.003']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner002" geometry={nodes['light-1-inner002'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover002" geometry={nodes['light-2-cover002'].geometry} material={materials['light2-cover.002']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner002" geometry={nodes['light-2-inner002'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover002" geometry={nodes['light-3-cover002'].geometry} material={materials['light2-cover.001']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner002" geometry={nodes['light-3-inner002'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - 2-GURUH */}
        <group name="Empty008" position={[0, 0, -74.071]}>
          <mesh name="light-1-cover004" geometry={nodes['light-1-cover004'].geometry} material={materials['light2-cover.009']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner004" geometry={nodes['light-1-inner004'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover004" geometry={nodes['light-2-cover004'].geometry} material={materials['light2-cover.008']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner004" geometry={nodes['light-2-inner004'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover004" geometry={nodes['light-3-cover004'].geometry} material={materials['light2-cover.007']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner004" geometry={nodes['light-3-inner004'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - 3-GURUH */}
        <group name="Empty012" position={[0, 0, -113.243]}>
          <mesh name="light-1-cover006" geometry={nodes['light-1-cover006'].geometry} material={materials['light2-cover.018']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner006" geometry={nodes['light-1-inner006'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover006" geometry={nodes['light-2-cover006'].geometry} material={materials['light2-cover.017']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner006" geometry={nodes['light-2-inner006'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover006" geometry={nodes['light-3-cover006'].geometry} material={materials['light2-cover.016']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner006" geometry={nodes['light-3-inner006'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - 4-GURUH */}
        <group name="Empty016" position={[0, 0, -153.316]}>
          <mesh name="light-1-cover008" geometry={nodes['light-1-cover008'].geometry} material={materials['light2-cover.024']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner008" geometry={nodes['light-1-inner008'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover008" geometry={nodes['light-2-cover008'].geometry} material={materials['light2-cover.023']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner008" geometry={nodes['light-2-inner008'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover008" geometry={nodes['light-3-cover008'].geometry} material={materials['light2-cover.022']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner008" geometry={nodes['light-3-inner008'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - 5-GURUH */}
        <group name="Empty018" position={[0, 0, -192.145]}>
          <mesh name="light-1-cover010" geometry={nodes['light-1-cover010'].geometry} material={materials['light2-cover.030']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner010" geometry={nodes['light-1-inner010'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover010" geometry={nodes['light-2-cover010'].geometry} material={materials['light2-cover.029']} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-2-inner010" geometry={nodes['light-2-inner010'].geometry} material={lampMaterial} position={[-0.984, -14.998, 48.266]} />
          <mesh name="light-3-cover010" geometry={nodes['light-3-cover010'].geometry} material={materials['light2-cover.028']} position={[-2.984, -14.998, 48.266]} />
          <mesh name="light-3-inner010" geometry={nodes['light-3-inner010'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - 6-GURUH */}
        <group name="Empty019" position={[0, 0, -211.891]}>
          <mesh name="light-1-cover011" geometry={nodes['light-1-cover011'].geometry} material={materials['light2-cover.033']} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-1-inner011" geometry={nodes['light-1-inner011'].geometry} material={lampMaterial} position={[1.016, -14.998, 48.266]} />
          <mesh name="light-2-cover011" geometry={nodes['light-2-cover011'].geometry} material={materials['light2-cover.032']} position={[-0.984, -16.644, 48.266]} />
          <mesh name="light-2-inner011" geometry={nodes['light-2-inner011'].geometry} material={lampMaterial} position={[-0.984, -16.644, 48.266]} />
          <mesh name="light-3-cover011" geometry={nodes['light-3-cover011'].geometry} material={materials['light2-cover.031']} position={[-2.984, -14.998, 48.266]} />
        </group>

        {/* LAMPALAR - OXIRGI */}
        <group name="Empty020" position={[0, 0, -211.942]}>
          <mesh name="light-3-inner012" geometry={nodes['light-3-inner012'].geometry} material={lampMaterial} position={[-2.984, -14.998, 48.266]} />
        </group>

      </group>
    </group>
  )
}

useGLTF.preload('/model3.glb')
